import React, { useEffect, useMemo, useState } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Salary.css";

const RUPEE_SYMBOL = "\u20B9";
const DEFAULT_SETTINGS = {
  max_loan_amount: 0,
  max_tenure_months: 6,
  interest_rate: 0,
};

function SalaryAdvances() {
  const [loading, setLoading] = useState(true);
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [currentUserHasActiveLoan, setCurrentUserHasActiveLoan] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionError, setRejectionError] = useState("");
  const [formData, setFormData] = useState({
    user_id: "",
    loan_amount: "",
    tenure_months: "",
    start_month: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get("/salary-advances");
      if (response.data.success) {
        setAdvances(response.data.data.advances || []);
        setEmployees(response.data.data.employees || []);
        setIsAdmin(Boolean(response.data.data.is_admin));
        setSettings(response.data.data.settings || DEFAULT_SETTINGS);
        setCurrentUserHasActiveLoan(
          Boolean(response.data.data.current_user_has_active_loan),
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load salary advances",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, fractionDigits = 0) => {
    const numericAmount = parseFloat(amount || 0);

    return `${RUPEE_SYMBOL}${numericAmount.toLocaleString("en-IN", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}`;
  };

  const formatRate = (rate) =>
    parseFloat(rate || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (isAdmin && !formData.user_id) {
      nextErrors.user_id = "Employee is required";
    }

    if (!formData.loan_amount || formData.loan_amount <= 0) {
      nextErrors.loan_amount = "Valid loan amount is required";
    } else if (
      settings.max_loan_amount > 0 &&
      parseFloat(formData.loan_amount) > parseFloat(settings.max_loan_amount)
    ) {
      nextErrors.loan_amount = `Loan amount cannot exceed ${formatCurrency(
        settings.max_loan_amount,
      )}`;
    }

    if (
      !formData.tenure_months ||
      formData.tenure_months < 1 ||
      formData.tenure_months > settings.max_tenure_months
    ) {
      nextErrors.tenure_months = `Tenure must be between 1-${settings.max_tenure_months} months`;
    }

    if (!formData.start_month) {
      nextErrors.start_month = "Start month is required";
    }

    if (!isAdmin && currentUserHasActiveLoan) {
      nextErrors.loan_amount =
        "You already have an active loan. Clear it before applying for a new one.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      loan_amount: "",
      tenure_months: "",
      start_month: "",
    });
    setErrors({});
  };

  const handleSubmitAdd = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await api.post("/salary-advances", formData);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to submit loan request",
      });
    }
  };

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Approve Loan?",
      text: "Are you sure you want to approve this loan request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await api.put(`/salary-advances/${id}/approve`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to approve loan",
      });
    }
  };

  const handleRejectClick = (advance) => {
    setSelectedAdvance(advance);
    setRejectionReason("");
    setRejectionError("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError("Rejection reason is required");
      return;
    }

    try {
      const response = await api.put(
        `/salary-advances/${selectedAdvance.id}/reject`,
        {
          rejection_reason: rejectionReason,
        },
      );
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Rejected",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        setShowRejectModal(false);
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to reject loan",
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This loan record will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6557ff",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await api.delete(`/salary-advances/${id}`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete loan",
      });
    }
  };

  const calcEmi = () => {
    if (
      formData.loan_amount &&
      formData.tenure_months &&
      formData.tenure_months > 0
    ) {
      return (
        parseFloat(formData.loan_amount) / parseInt(formData.tenure_months, 10)
      ).toFixed(2);
    }

    return "0.00";
  };

  const canOpenApplyModal = () => {
    if (!isAdmin && currentUserHasActiveLoan) {
      Swal.fire({
        icon: "warning",
        title: "Active Loan Exists",
        text: "You already have an active loan. Please clear the existing loan before applying for a new one.",
      });
      return false;
    }

    return true;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      default:
        return "status-pending";
    }
  };

  const formatMonth = (dateStr) => {
    if (!dateStr) {
      return "N/A";
    }

    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      year: "numeric",
    });
  };

  const statusCounts = useMemo(
    () =>
      advances.reduce(
        (counts, advance) => {
          const status = advance.status || "pending";
          counts.total += 1;

          if (status === "approved") {
            counts.approved += 1;
          } else if (status === "rejected") {
            counts.rejected += 1;
          } else {
            counts.pending += 1;
          }

          return counts;
        },
        { total: 0, approved: 0, pending: 0, rejected: 0 },
      ),
    [advances],
  );

  const totalActiveExposure = useMemo(
    () =>
      advances
        .filter((advance) => advance.status === "approved")
        .reduce(
          (sum, advance) => sum + parseFloat(advance.remaining_amount || 0),
          0,
        ),
    [advances],
  );

  const advanceColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    ...(isAdmin
      ? [
          {
            header: "Employee",
            render: (advance) => (
              <div className="employee-info-cell">
                <div className="employee-avatar-small">
                  {advance.employee_name.charAt(0)}
                </div>
                <div className="employee-meta-block">
                  <span className="employee-name-text">{advance.employee_name}</span>
                  <span className="employee-code">({advance.employee_code})</span>
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      header: "Loan Amount",
      render: (advance) => (
        <span className="amount-value">{formatCurrency(advance.loan_amount)}</span>
      ),
    },
    {
      header: "Tenure (Months)",
      render: (advance) => `${advance.tenure_months} months`,
    },
    {
      header: "EMI Amount",
      render: (advance) => formatCurrency(advance.emi_amount, 2),
    },
    {
      header: "Remaining",
      render: (advance) => (
        <span
          className={`remaining-amount ${
            parseFloat(advance.remaining_amount) === 0 ? "cleared" : ""
          }`}
        >
          {formatCurrency(advance.remaining_amount)}
        </span>
      ),
    },
    {
      header: "Start Month",
      render: (advance) => formatMonth(advance.start_month),
    },
    {
      header: "Status",
      render: (advance) => (
        <span className={`status-badge ${getStatusClass(advance.status)}`}>
          {advance.status.charAt(0).toUpperCase() + advance.status.slice(1)}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            header: "Actions",
            render: (advance) => (
              <CommonActionMenu
                ariaLabel={`Actions for advance ${advance.id}`}
                actions={[
                  advance.status === "pending"
                    ? {
                        label: "Approve",
                        icon: "fas fa-check",
                        variant: "success",
                        onClick: () => handleApprove(advance.id),
                      }
                    : null,
                  advance.status === "pending"
                    ? {
                        label: "Reject",
                        icon: "fas fa-times",
                        variant: "warning",
                        onClick: () => handleRejectClick(advance),
                      }
                    : null,
                  {
                    label: "Delete",
                    icon: "fas fa-trash",
                    variant: "danger",
                    onClick: () => handleDelete(advance.id),
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="salary-advances-container">
        <div className="salary-advances-hero">
          <div className="salary-advances-hero-copy">
            <span className="salary-advances-eyebrow">Advance management</span>
            <h1>Salary Advances</h1>
            <p>
              Track active loan requests, monitor repayment exposure, and manage
              employee salary advances with a cleaner approval workflow.
            </p>
          </div>
          <div className="salary-advances-stats">
            <div className="salary-advance-stat-card">
              <span className="salary-advance-stat-label">Total requests</span>
              <strong>{statusCounts.total}</strong>
            </div>
            <div className="salary-advance-stat-card">
              <span className="salary-advance-stat-label">Pending review</span>
              <strong>{statusCounts.pending}</strong>
            </div>
            <div className="salary-advance-stat-card">
              <span className="salary-advance-stat-label">Active exposure</span>
              <strong>{formatCurrency(totalActiveExposure)}</strong>
            </div>
          </div>
        </div>

        <div className="salary-advance-policy-banner">
          <div className="salary-advance-policy-copy">
            <span className="salary-advance-policy-title">Current company policy</span>
            <p>
              Maximum loan {formatCurrency(settings.max_loan_amount)} with up to{" "}
              {settings.max_tenure_months || 6} months tenure and {formatRate(settings.interest_rate)}%
              {" "}annual interest.
            </p>
          </div>
          {!isAdmin && currentUserHasActiveLoan && (
            <span className="salary-advance-policy-alert">
              You already have an active approved loan.
            </span>
          )}
        </div>

        <div className="advances-card">
          <div className="card-header-custom">
            <div className="header-copy">
              <span className="section-kicker">Loan register</span>
              <h3>Salary Advances</h3>
              <p>
                Review request status, repayment progress, and employee-wise
                salary advances in one place.
              </p>
            </div>
            <button
              className="btn-add-advance"
              onClick={() => {
                if (!canOpenApplyModal()) {
                  return;
                }

                resetForm();
                setShowAddModal(true);
              }}
            >
              + Apply For Loan
            </button>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={advanceColumns}
              data={advances}
              tableClassName="advances-table-clean"
              noDataClassName="text-center py-5 no-data"
              emptyMessage="No salary advances found"
            />
          </div>
        </div>

        {showAddModal && (
          <CommonModal
            onClose={() => setShowAddModal(false)}
            title="Add New Loan"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom"
            footerClassName="modal-footer-custom"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="modal-footer-custom"
                cancelLabel="Close"
                submitLabel="Apply"
                onCancel={() => setShowAddModal(false)}
                onSubmit={handleSubmitAdd}
                submitType="button"
              />
            }
          >
            <form onSubmit={handleSubmitAdd}>
              <div>
                {isAdmin && (
                  <div className="form-row-custom">
                    <label>
                      Employee<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <select
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleInputChange}
                        className={errors.user_id ? "error" : ""}
                      >
                        <option value="">Select Employee</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name} ({employee.code})
                          </option>
                        ))}
                      </select>
                      {errors.user_id && <span className="error-msg">{errors.user_id}</span>}
                    </div>
                  </div>
                )}

                <div className="form-row-custom">
                  <label>
                    Loan Amount<span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <div className="input-with-prefix">
                      <span className="input-prefix">{RUPEE_SYMBOL}</span>
                      <input
                        type="number"
                        name="loan_amount"
                        value={formData.loan_amount}
                        onChange={handleInputChange}
                        placeholder="Enter loan amount"
                        min="1"
                        max={settings.max_loan_amount || undefined}
                        className={errors.loan_amount ? "error" : ""}
                      />
                    </div>
                    {errors.loan_amount && <span className="error-msg">{errors.loan_amount}</span>}
                    {!errors.loan_amount && settings.max_loan_amount > 0 && (
                      <span className="helper-text">
                        Maximum allowed: {formatCurrency(settings.max_loan_amount)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row-custom">
                  <label>
                    Tenure<span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <select
                      name="tenure_months"
                      value={formData.tenure_months}
                      onChange={handleInputChange}
                      className={errors.tenure_months ? "error" : ""}
                    >
                      <option value="">Select tenure</option>
                      {Array.from(
                        { length: settings.max_tenure_months || 6 },
                        (_, index) => index + 1,
                      ).map((month) => (
                        <option key={month} value={month}>
                          {month} month{month > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                    {errors.tenure_months && (
                      <span className="error-msg">{errors.tenure_months}</span>
                    )}
                  </div>
                </div>

                <div className="form-row-custom">
                  <label>
                    Start Month<span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="month"
                      name="start_month"
                      value={formData.start_month}
                      onChange={handleInputChange}
                      className={errors.start_month ? "error" : ""}
                    />
                    {errors.start_month && <span className="error-msg">{errors.start_month}</span>}
                  </div>
                </div>

                {formData.loan_amount && formData.tenure_months && (
                  <div className="emi-preview">
                    <div className="emi-preview-content">
                      <i className="fas fa-calculator"></i>
                      <span>
                        Monthly EMI: <strong>{formatCurrency(calcEmi(), 2)}</strong>
                      </span>
                    </div>
                  </div>
                )}

                {!isAdmin && currentUserHasActiveLoan && (
                  <div className="emi-preview salary-advance-warning-banner">
                    <div className="emi-preview-content salary-advance-warning-content">
                      <i className="fas fa-circle-exclamation"></i>
                      <span>
                        An active approved loan already exists for your account.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </CommonModal>
        )}

        {showRejectModal && selectedAdvance && (
          <CommonModal
            onClose={() => setShowRejectModal(false)}
            title="Reject Loan Request"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom"
            footerClassName="modal-footer-custom"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="modal-footer-custom"
                cancelLabel="Close"
                submitLabel="Reject Loan"
                onCancel={() => setShowRejectModal(false)}
                onSubmit={handleRejectSubmit}
                submitType="button"
                submitClassName="btn-reject-submit"
              />
            }
          >
            <div className="reject-info-box">
              <p>
                <strong>Employee:</strong> {selectedAdvance.employee_name}
              </p>
              <p>
                <strong>Loan Amount:</strong> {formatCurrency(selectedAdvance.loan_amount)}
              </p>
            </div>
            <div className="form-row-custom salary-advance-reject-row">
              <label>
                Reason<span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <textarea
                  value={rejectionReason}
                  onChange={(event) => {
                    setRejectionReason(event.target.value);
                    setRejectionError("");
                  }}
                  placeholder="Enter rejection reason..."
                  rows="4"
                  className={rejectionError ? "error" : ""}
                ></textarea>
                {rejectionError && <span className="error-msg">{rejectionError}</span>}
              </div>
            </div>
          </CommonModal>
        )}
      </div>
    </MainLayout>
  );
}

export default SalaryAdvances;
